/**
 * @file resolveContractEndDateSaveOutcome.ts
 * @description Pure decision helper for the Contract End Date section's
 *   post-save state transition (SE-4881). Maps the boolean result of the
 *   `editContractEndDate` mutation to the next `{ isEditing, errorMessage }`
 *   the container should apply:
 *   - success (`ok === true`)  → exit edit mode, clear any error
 *   - failure (`ok === false`) → stay in edit mode, surface a save error
 *
 *   Extracted from `ContractEndDateSectionContainer.handleSaveEdit` so the
 *   success/failure branches can be unit-tested deterministically — the
 *   integrated edit→save flow involves an async `setState` that settles
 *   after an awaited mutation, which the React 19 + react-test-renderer
 *   env can't assert on without a flaky promise-settling `act`. The
 *   container consumes this helper; the rendered failure message is
 *   covered by `ContractEndDateSectionView.test`, and the full async flow
 *   by the Playwright E2E during verification.
 *
 *   Lives in `features/contracts/utils` (not next to the component)
 *   because component directories are restricted to index/Container/View
 *   files by the project's component-structure lint rule.
 * @module features/contracts/utils/resolveContractEndDateSaveOutcome
 */

/** User-facing copy when the save mutation reports failure. */
export const CONTRACT_END_DATE_SAVE_FAILED_MESSAGE =
  "Couldn't save the contract end date. Try again.";

/** The next inline-edit state the container applies after a save attempt. */
export interface ContractEndDateSaveOutcome {
  /** Whether the section should remain in inline-edit mode. */
  readonly isEditing: boolean;
  /** Inline error message to show, or null to clear it. */
  readonly errorMessage: string | null;
}

/**
 * Resolves the post-save edit/error state from the mutation result.
 * @param ok - Whether `editContractEndDate` reported success
 * @returns On success, exit edit mode with no error; on failure, stay in
 *   edit mode and surface {@link CONTRACT_END_DATE_SAVE_FAILED_MESSAGE}
 */
export function resolveContractEndDateSaveOutcome(
  ok: boolean
): ContractEndDateSaveOutcome {
  return ok
    ? { isEditing: false, errorMessage: null }
    : { isEditing: true, errorMessage: CONTRACT_END_DATE_SAVE_FAILED_MESSAGE };
}
